import React, { useState, useMemo } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { Button, Empty, Spin, Select, DatePicker } from 'antd-v5';
import { connect } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import StandardLayoutV2 from '../../../Layout/StandardLayoutV2';
import EventCard from './components/EventCard';
import EventLinksModal from './components/EventLinksModal';
import { useEvents } from '../../../../hooks/query/useEvents';
import { useMerchantStores } from '../../../../hooks/query/useStores';
import './styles.scss';

// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { RangePicker } = DatePicker;

// Helper to check if event is currently in progress
const isEventInProgress = event => {
  const now = dayjs();
  const startDate = event.startDate || event.start_date;
  const endDate = event.endDate || event.end_date;

  if (!startDate || !endDate) return false;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return start.isSameOrBefore(now, 'day') && end.isSameOrAfter(now, 'day');
};

const Events = () => {
  const history = useHistory();
  const { events: eventsFlag } = useFlags();

  // Redirect if feature flag is disabled
  if (!eventsFlag) {
    return <Redirect to="/" />;
  }

  // Filter state
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  // Event Links Modal state
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Pagination state (Load More)
  const [inProgressLimit, setInProgressLimit] = useState(8);
  const [upcomingLimit, setUpcomingLimit] = useState(8);
  const [pastLimit, setPastLimit] = useState(8);

  // Fetch stores for filter dropdown
  const { data: storesData } = useMerchantStores({
    useQueryOptions: {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes to reduce refetching
    },
  });
  const stores = storesData?.results || storesData?.data || [];

  // Build query params for API
  const queryParams = useMemo(() => {
    const params = {
      offset: 0,
      limit: 100, // API max is 100, pagination handled client-side
    };
    if (selectedStoreIds.length > 0) {
      // API expects comma-separated string, not array
      params.storeIds = selectedStoreIds.join(',');
    }
    if (dateRange[0]) {
      params.fromDate = dateRange[0].format('YYYY-MM-DD');
    }
    if (dateRange[1]) {
      params.toDate = dateRange[1].format('YYYY-MM-DD');
    }
    return params;
  }, [selectedStoreIds, dateRange]);

  // Fetch events with filters
  const {
    data: eventsData,
    isLoading,
    error,
  } = useEvents({
    query: queryParams,
    useQueryOptions: {
      staleTime: 30 * 1000, // Cache for 30 seconds
    },
  });

  const events = eventsData?.data || eventsData || [];

  // Split events into in progress, upcoming, and past
  const { inProgressEvents, upcomingEvents, pastEvents } = useMemo(() => {
    const now = dayjs();
    const inProgress = [];
    const upcoming = [];
    const past = [];

    events.forEach(event => {
      const eventStartDate = event.startDate || event.start_date;
      const eventEndDate = event.endDate || event.end_date;

      // Check if event is in progress (started but not ended)
      if (isEventInProgress(event)) {
        inProgress.push(event);
      } else if (eventEndDate && dayjs(eventEndDate).isBefore(now, 'day')) {
        // Past: end date is before today
        past.push(event);
      } else if (eventStartDate && dayjs(eventStartDate).isAfter(now, 'day')) {
        // Upcoming: start date is after today
        upcoming.push(event);
      } else {
        // Default to upcoming for events without proper dates
        upcoming.push(event);
      }
    });

    // Sort function: alphabetically by name
    const sortFn = (a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    };

    return {
      inProgressEvents: inProgress.sort(sortFn),
      upcomingEvents: upcoming.sort(sortFn),
      pastEvents: past.sort(sortFn),
    };
  }, [events]);

  // Paginated events for display
  const displayedInProgress = inProgressEvents.slice(0, inProgressLimit);
  const displayedUpcoming = upcomingEvents.slice(0, upcomingLimit);
  const displayedPast = pastEvents.slice(0, pastLimit);
  const hasMoreInProgress = inProgressEvents.length > inProgressLimit;
  const hasMoreUpcoming = upcomingEvents.length > upcomingLimit;
  const hasMorePast = pastEvents.length > pastLimit;

  const handleAddEvent = () => {
    history.push('/Configuration/events/add-event');
  };

  const handleEventClick = eventId => {
    history.push(`/Configuration/events/event-details/${eventId}`);
  };

  const handleLinksClick = event => {
    setSelectedEvent(event);
    setShowLinksModal(true);
  };

  const breadcrumb = [{ title: 'Setup' }, { title: 'Events' }];

  if (error) {
    return (
      <StandardLayoutV2
        title="Events"
        breadcrumb={breadcrumb}
        classProps="events-page"
        containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
      >
        <div className="events-error">
          <Empty description="Failed to load events. Please try again." />
        </div>
      </StandardLayoutV2>
    );
  }

  return (
    <StandardLayoutV2
      title="Events"
      breadcrumb={breadcrumb}
      classProps="events-page"
      containerStyles={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px' }}
    >
      {/* Header Actions */}
      <div className="events-header flex-row-spacebetween-nowrap m-btm-24">
        <div className="flex-row-nospacebetween-nowrap gap-16">
          <Select
            mode="multiple"
            allowClear
            placeholder="Filter by Store"
            value={selectedStoreIds}
            onChange={setSelectedStoreIds}
            style={{ minWidth: 180 }}
            maxTagCount={1}
            maxTagPlaceholder={omittedValues => `+${omittedValues.length} more`}
          >
            {stores.map(store => (
              <Select.Option key={store.id} value={store.id}>
                {store.name}
              </Select.Option>
            ))}
          </Select>
          <RangePicker
            value={dateRange}
            onChange={dates => setDateRange(dates || [null, null])}
            placeholder={['Start Date', 'End Date']}
            allowClear
          />
        </div>
        <Button type="primary" onClick={handleAddEvent}>
          + Add Event
        </Button>
      </div>

      {isLoading ? (
        <div className="events-loading flex-col-center">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* In Progress Events Section */}
          {inProgressEvents.length > 0 && (
            <div className="events-section m-btm-32">
              <h3 className="events-section-title m-btm-16">In Progress Events ({inProgressEvents.length})</h3>
              <div className="events-grid">
                {displayedInProgress.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onClick={() => handleEventClick(event.id)}
                    onLinksClick={handleLinksClick}
                  />
                ))}
              </div>
              {hasMoreInProgress && (
                <div className="events-load-more m-top-24">
                  <Button onClick={() => setInProgressLimit(prev => prev + 8)}>
                    Load More ({inProgressEvents.length - inProgressLimit} remaining)
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Upcoming Events Section */}
          <div className="events-section m-btm-32">
            <h3 className="events-section-title m-btm-16">
              Upcoming Events {upcomingEvents.length > 0 && `(${upcomingEvents.length})`}
            </h3>
            {upcomingEvents.length > 0 ? (
              <>
                <div className="events-grid">
                  {displayedUpcoming.map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onClick={() => handleEventClick(event.id)}
                      onLinksClick={handleLinksClick}
                    />
                  ))}
                </div>
                {hasMoreUpcoming && (
                  <div className="events-load-more m-top-24">
                    <Button onClick={() => setUpcomingLimit(prev => prev + 8)}>
                      Load More ({upcomingEvents.length - upcomingLimit} remaining)
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Empty description="No upcoming events" />
            )}
          </div>

          {/* Past Events Section */}
          <div className="events-section">
            <h3 className="events-section-title m-btm-16">
              Past Events {pastEvents.length > 0 && `(${pastEvents.length})`}
            </h3>
            {pastEvents.length > 0 ? (
              <>
                <div className="events-grid">
                  {displayedPast.map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      isPast
                      onClick={() => handleEventClick(event.id)}
                      onLinksClick={handleLinksClick}
                    />
                  ))}
                </div>
                {hasMorePast && (
                  <div className="events-load-more m-top-24">
                    <Button onClick={() => setPastLimit(prev => prev + 8)}>
                      Load More ({pastEvents.length - pastLimit} remaining)
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Empty description="No past events" />
            )}
          </div>
        </>
      )}

      {/* Event Links Modal */}
      <EventLinksModal open={showLinksModal} onClose={() => setShowLinksModal(false)} event={selectedEvent} />
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(Events);
